import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, ReferenceLine
} from 'recharts';
import { Activity, Wifi, TrendingUp, TrendingDown, Server, AlertTriangle } from 'lucide-react';
import { bwColor } from './topology';
import { useTopology } from './TopologyContext';

const MAX_POINTS = 60; // 2 min at 2s refresh
const LINK_GBPS = 40;

// Per-node line colors. Cluster A → cyan family, Cluster B → violet family,
// node14/15 (unassigned) → neutral greys. rx/tx/total share a hue.
const NODE_COLORS = {
  'node9':  { rx: '#67e8f9', tx: '#0e7490', total: '#22d3ee' },
  'node10': { rx: '#5eead4', tx: '#0f766e', total: '#14b8a6' },
  'node11': { rx: '#7dd3fc', tx: '#0369a1', total: '#0ea5e9' },
  'node12': { rx: '#93c5fd', tx: '#1d4ed8', total: '#3b82f6' },
  'node13': { rx: '#a5f3fc', tx: '#155e75', total: '#06b6d4' },
  'node14': { rx: '#9ca3af', tx: '#4b5563', total: '#6b7280' },
  'node15': { rx: '#cbd5e1', tx: '#475569', total: '#64748b' },
  'node16': { rx: '#c4b5fd', tx: '#6d28d9', total: '#a78bfa' },
  'node17': { rx: '#d8b4fe', tx: '#7e22ce', total: '#c084fc' },
};

// Never index NODE_COLORS directly — the topology is dynamic (nodes can be
// renamed/added), and a missing entry would throw during render. Derive a
// stable hue from the node name as a fallback so it degrades gracefully.
function nodeColors(node) {
  if (NODE_COLORS[node]) return NODE_COLORS[node];
  let h = 0;
  for (let i = 0; i < (node || '').length; i++) h = (h * 31 + node.charCodeAt(i)) % 360;
  return { rx: `hsl(${h},70%,62%)`, tx: `hsl(${h},70%,42%)`, total: `hsl(${h},70%,55%)` };
}

function bwLabel(gbps) {
  if (gbps >= 25) return { text: 'OPTIMAL', color: '#22c55e' };
  if (gbps >= 10) return { text: 'MODERATE', color: '#eab308' };
  if (gbps > 0)   return { text: 'LOW', color: '#3b82f6' };
  return { text: 'IDLE', color: '#6b7280' };
}

function GbpsBar({ value, max = LINK_GBPS }) {
  const pct = Math.min((value / max) * 100, 100);
  const color = bwColor(value);
  return (
    <div style={{
      width: '100%', height: 8, backgroundColor: '#1f2937',
      borderRadius: 4, overflow: 'hidden',
    }}>
      <div style={{
        width: `${pct}%`, height: '100%', backgroundColor: color,
        boxShadow: `0 0 6px ${color}`,
        transition: 'width 0.5s ease-out',
        borderRadius: 4,
      }} />
    </div>
  );
}

function StatBadge({ label, value, unit = 'Gb/s' }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        fontSize: 9, color: '#6b7280', textTransform: 'uppercase',
        letterSpacing: 1, marginBottom: 2,
      }}>{label}</div>
      <div style={{
        fontSize: 12, fontFamily: 'monospace', fontWeight: 700, color: '#fff',
      }}>
        {value ?? '--'}
        <span style={{ color: '#9ca3af', fontSize: 10, marginLeft: 3 }}>{unit}</span>
      </div>
    </div>
  );
}

function NodeCard({ node, latest, history }) {
  const { CLUSTERS, NODE_TO_CLUSTER } = useTopology();
  const colors = nodeColors(node);
  const clusterId = NODE_TO_CLUSTER[node] || null;
  const cluster = clusterId ? CLUSTERS[clusterId] : null;
  const rx = latest?.rx_gbps ?? 0;
  const tx = latest?.tx_gbps ?? 0;
  const total = latest?.total_gbps ?? 0;
  const cpu = latest?.cpu ?? null;
  const pf = latest?.pf_procs ?? 0;
  const status = latest?.status;
  const { text: bwText, color: bwTextColor } = bwLabel(total);

  const pctLink = ((total / LINK_GBPS) * 100).toFixed(0);

  const totals = history.map(h => h.total_gbps).filter(v => v != null);
  const minVal = totals.length ? Math.min(...totals).toFixed(2) : '--';
  const maxVal = totals.length ? Math.max(...totals).toFixed(2) : '--';
  const avgVal = totals.length ? (totals.reduce((a, b) => a + b, 0) / totals.length).toFixed(2) : '--';

  if (status === 'down') {
    return (
      <div style={{
        backgroundColor: '#0f172a', border: '1px solid #7f1d1d',
        borderRadius: 16, padding: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <AlertTriangle style={{ width: 16, height: 16, color: '#ef4444' }} />
          <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: '#fca5a5' }}>{node}</span>
          <span style={{
            marginLeft: 'auto', fontSize: 10, fontWeight: 700,
            backgroundColor: '#7f1d1d', color: '#fecaca',
            padding: '2px 8px', borderRadius: 4, letterSpacing: 0.5,
          }}>UNREACHABLE</span>
        </div>
        <p style={{ fontSize: 11, color: '#6b7280' }}>Node is not responding to SSH</p>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: '#0f172a', border: '1px solid #334155',
      borderRadius: 16, padding: 16, display: 'flex', flexDirection: 'column', gap: 12,
      boxShadow: `0 0 20px ${colors.total}18`,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Server style={{ width: 16, height: 16, color: colors.total }} />
        <span style={{
          fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: '#fff',
        }}>{node}</span>
        {clusterId ? (
          <span style={{
            fontSize: 9, fontWeight: 700, color: cluster.color,
            backgroundColor: `${cluster.color}1a`,
            padding: '1px 6px', borderRadius: 4, letterSpacing: 0.4,
          }}>{clusterId}</span>
        ) : (
          <span
            title="Not assigned to a scheduling cluster"
            style={{
              fontSize: 9, fontWeight: 700, color: '#9ca3af',
              backgroundColor: '#37415133',
              padding: '1px 6px', borderRadius: 4, letterSpacing: 0.4,
            }}
          >UNASSIGNED</span>
        )}
        <span style={{
          marginLeft: 'auto', fontSize: 10, fontWeight: 700,
          fontFamily: 'monospace', color: bwTextColor,
        }}>{bwText}</span>
        {pf > 0 && (
          <span style={{
            fontSize: 10, fontFamily: 'monospace',
            backgroundColor: '#1e3a8a', color: '#bfdbfe',
            padding: '1px 6px', borderRadius: 4,
          }}>{pf} PF</span>
        )}
      </div>

      {/* Link utilization bar */}
      <div>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          fontSize: 11, color: '#9ca3af', marginBottom: 4,
        }}>
          <span>Link Utilization</span>
          <span style={{ fontFamily: 'monospace', color: bwColor(total) }}>{pctLink}%</span>
        </div>
        <GbpsBar value={total} />
      </div>

      {/* RX / TX live */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ backgroundColor: '#1e293b', borderRadius: 8, padding: 8, textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, marginBottom: 4 }}>
            <TrendingDown style={{ width: 12, height: 12, color: colors.rx }} />
            <span style={{ fontSize: 11, color: '#9ca3af' }}>RX</span>
          </div>
          <div style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 18, color: colors.rx }}>
            {rx.toFixed(2)}
          </div>
          <div style={{ fontSize: 10, color: '#6b7280' }}>Gb/s</div>
        </div>
        <div style={{ backgroundColor: '#1e293b', borderRadius: 8, padding: 8, textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, marginBottom: 4 }}>
            <TrendingUp style={{ width: 12, height: 12, color: colors.tx }} />
            <span style={{ fontSize: 11, color: '#9ca3af' }}>TX</span>
          </div>
          <div style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 18, color: colors.tx }}>
            {tx.toFixed(2)}
          </div>
          <div style={{ fontSize: 10, color: '#6b7280' }}>Gb/s</div>
        </div>
      </div>

      {/* Session stats */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4,
        backgroundColor: '#1e293b', borderRadius: 8, padding: 8,
      }}>
        <StatBadge label="Total" value={total.toFixed(2)} />
        <StatBadge label="Min" value={minVal} />
        <StatBadge label="Max" value={maxVal} />
        <StatBadge label="Avg" value={avgVal} />
      </div>

      {/* CPU */}
      {cpu !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, color: '#6b7280' }}>CPU</span>
          <div style={{ flex: 1, height: 6, backgroundColor: '#1e293b', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{
              width: `${cpu}%`, height: '100%', borderRadius: 3,
              backgroundColor: cpu >= 85 ? '#ef4444' : cpu >= 60 ? '#eab308' : '#22c55e',
              transition: 'width 0.5s ease-out',
            }} />
          </div>
          <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#9ca3af' }}>{cpu.toFixed(1)}%</span>
        </div>
      )}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      backgroundColor: '#0f172a', border: '1px solid #334155',
      borderRadius: 8, padding: 12, fontSize: 11, fontFamily: 'monospace',
      boxShadow: '0 10px 25px rgba(0,0,0,0.4)',
    }}>
      <p style={{ color: '#9ca3af', marginBottom: 8 }}>{label}</p>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
          <span style={{ color: p.color }}>{p.name}</span>
          <span style={{ color: '#fff' }}>
            {typeof p.value === 'number' ? p.value.toFixed(2) : '--'} Gb/s
          </span>
        </div>
      ))}
    </div>
  );
};

export default function Network40GDashboard() {
  const { ALL_NODES: NODES } = useTopology();
  const [nodeHistory, setNodeHistory] = useState(() =>
    Object.fromEntries(NODES.map(n => [n, []]))
  );
  const [latest, setLatest] = useState(() =>
    Object.fromEntries(NODES.map(n => [n, null]))
  );
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState('total'); // rx | tx | total
  // User can isolate specific nodes on the chart. Empty set = show all.
  const [hiddenNodes, setHiddenNodes] = useState(() => new Set());
  const tickRef = useRef(0);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch('/api/network40g');
      if (!res.ok) return;
      const data = await res.json();

      tickRef.current += 1;
      const tick = tickRef.current;
      const label = new Date().toLocaleTimeString('en-US', {
        hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit',
      });

      setLatest(prev => {
        const next = { ...prev };
        for (const node of NODES) {
          if (data[node]) next[node] = data[node];
        }
        return next;
      });

      setNodeHistory(prev => {
        const next = { ...prev };
        for (const node of NODES) {
          if (data[node] && data[node].status !== 'down') {
            const entry = { tick, ...data[node] };
            next[node] = [...prev[node].slice(-(MAX_POINTS - 1)), entry];
          }
        }
        return next;
      });

      setChartData(prev => {
        const point = { label };
        for (const node of NODES) {
          if (data[node] && data[node].status !== 'down') {
            point[`${node}_rx`] = data[node].rx_gbps ?? 0;
            point[`${node}_tx`] = data[node].tx_gbps ?? 0;
            point[`${node}_total`] = data[node].total_gbps ?? 0;
          }
        }
        return [...prev.slice(-(MAX_POINTS - 1)), point];
      });

      setLastUpdated(new Date());
      setLoading(false);
    } catch (e) {
      console.error('40G fetch error:', e);
      setLoading(false);
    }
  }, [NODES]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 2000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Aggregate totals for summary bar
  const clusterTotal = NODES.reduce((sum, n) => sum + (latest[n]?.total_gbps ?? 0), 0);
  const clusterMax = NODES.length * LINK_GBPS;
  const clusterPct = ((clusterTotal / clusterMax) * 100).toFixed(1);

  const metricLabels = {
    total: 'Combined (RX+TX)',
    rx: 'RX (Inbound)',
    tx: 'TX (Outbound)',
  };

  const toggleNode = (node) => {
    setHiddenNodes(prev => {
      const next = new Set(prev);
      if (next.has(node)) next.delete(node); else next.add(node);
      return next;
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Header */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center',
        justifyContent: 'space-between', gap: 16,
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Wifi style={{ width: 20, height: 20, color: '#22d3ee' }} />
            <h2 style={{ color: '#fff', fontSize: 20, fontWeight: 700, margin: 0 }}>40G Fabric Monitor</h2>
            <span style={{
              fontSize: 10, fontFamily: 'monospace', color: '#6b7280',
              backgroundColor: '#1e293b', padding: '2px 8px', borderRadius: 4,
            }}>Mellanox 40G</span>
          </div>
          <p style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
            {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : 'Connecting...'}
            {' · '}Live 2s refresh · {MAX_POINTS}s rolling window
          </p>
        </div>

        {/* Cluster aggregate */}
        <div style={{
          backgroundColor: '#0f172a', border: '1px solid #334155',
          borderRadius: 12, padding: '10px 16px', minWidth: 240,
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            fontSize: 11, color: '#9ca3af', marginBottom: 4,
          }}>
            <span>Cluster Aggregate</span>
            <span style={{ fontFamily: 'monospace', color: bwColor(clusterTotal / NODES.length) }}>
              {clusterTotal.toFixed(2)} / {clusterMax} Gb/s
            </span>
          </div>
          <GbpsBar value={clusterTotal} max={clusterMax} />
          <div style={{
            textAlign: 'center', fontSize: 11, fontFamily: 'monospace',
            color: '#6b7280', marginTop: 4,
          }}>
            {clusterPct}% fabric utilization
          </div>
        </div>
      </div>

      {/* Per-node cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 16,
      }}>
        {NODES.map(node => (
          <NodeCard
            key={node}
            node={node}
            latest={latest[node]}
            history={nodeHistory[node]}
          />
        ))}
      </div>

      {/* Chart */}
      <div style={{
        backgroundColor: '#0f172a', border: '1px solid #334155',
        borderRadius: 16, padding: 16,
      }}>
        <div style={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center',
          justifyContent: 'space-between', gap: 12, marginBottom: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Activity style={{ width: 16, height: 16, color: '#22d3ee' }} />
            <span style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>Throughput History</span>
            <span style={{ color: '#6b7280', fontSize: 11 }}>— {metricLabels[selectedMetric]}</span>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {['total', 'rx', 'tx'].map(m => (
              <button
                key={m}
                onClick={() => setSelectedMetric(m)}
                style={{
                  padding: '4px 12px', borderRadius: 4, border: 'none',
                  fontSize: 11, fontFamily: 'monospace', fontWeight: 700,
                  cursor: 'pointer', transition: 'all 0.15s',
                  backgroundColor: selectedMetric === m ? '#0891b2' : '#1f2937',
                  color: selectedMetric === m ? '#fff' : '#9ca3af',
                }}
              >
                {m.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Per-node toggle chips — click a node to hide/show its line */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12,
          paddingBottom: 12, borderBottom: '1px solid #1e293b',
        }}>
          {NODES.map(node => {
            const isHidden = hiddenNodes.has(node);
            const color = nodeColors(node)[selectedMetric];
            return (
              <button
                key={node}
                onClick={() => toggleNode(node)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '3px 8px', borderRadius: 4,
                  border: `1px solid ${isHidden ? '#1e293b' : color}`,
                  background: 'transparent', cursor: 'pointer',
                  fontFamily: 'monospace', fontSize: 10,
                  color: isHidden ? '#4b5563' : '#d1d5db',
                  opacity: isHidden ? 0.5 : 1,
                  transition: 'all 0.15s',
                }}
              >
                <span style={{
                  width: 8, height: 8, borderRadius: 2,
                  backgroundColor: isHidden ? '#374151' : color,
                }} />
                {node}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            height: 192, color: '#6b7280', fontSize: 13,
          }}>
            <Activity style={{ width: 16, height: 16, marginRight: 8, animation: 'spin 2s linear infinite' }} />
            Collecting initial data...
          </div>
        ) : chartData.length < 2 ? (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            height: 192, color: '#6b7280', fontSize: 13,
          }}>
            Warming up — need 2+ samples...
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
              <XAxis
                dataKey="label"
                tick={{ fill: '#6b7280', fontSize: 10, fontFamily: 'monospace' }}
                interval="preserveStartEnd"
              />
              <YAxis
                domain={[0, LINK_GBPS]}
                tick={{ fill: '#6b7280', fontSize: 10, fontFamily: 'monospace' }}
                tickFormatter={v => `${v}G`}
                width={36}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, fontFamily: 'monospace', color: '#9ca3af' }} />
              <ReferenceLine y={25} stroke="#22c55e" strokeDasharray="4 4" strokeOpacity={0.4} />
              <ReferenceLine y={10} stroke="#eab308" strokeDasharray="4 4" strokeOpacity={0.4} />

              {NODES.filter(n => !hiddenNodes.has(n)).map(node => {
                const key = `${node}_${selectedMetric}`;
                return (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    name={`${node} ${selectedMetric.toUpperCase()}`}
                    stroke={nodeColors(node)[selectedMetric]}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, strokeWidth: 0 }}
                    connectNulls
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        )}

        {/* Legend for threshold lines */}
        <div style={{
          display: 'flex', gap: 16, justifyContent: 'flex-end',
          marginTop: 8, fontSize: 11, color: '#6b7280',
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 16, borderTop: '2px dashed #22c55e' }} />
            25 Gb/s optimal
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 16, borderTop: '2px dashed #eab308' }} />
            10 Gb/s moderate
          </span>
        </div>
      </div>
    </div>
  );
}
